import { ArchBaseService } from "@/common/api.service";

class TraditionalAuthService {
  constructor() {
    this.currentUser = null;
    this.token = null;
    this.loadFromStorage();
  }

  loadFromStorage() {
    this.token = localStorage.getItem("jwtToken");
    const userStr = localStorage.getItem("currentUser");
    if (userStr) {
      try {
        this.currentUser = JSON.parse(userStr);
      } catch (error) {
        console.error("Failed to parse user data:", error);
        this.currentUser = null;
      }
    }
  }

  async login(credentials) {
    try {
      ArchBaseService.init();

      const loginData = {
        email: credentials.email,
        password: credentials.password,
        usertype: "internal",
      };

      const response = await ArchBaseService.post("auth/login", loginData);

      if (response.status === 200 && response.data) {
        this.token = response.data.token;
        this.currentUser = response.data;

        // Store in localStorage
        localStorage.setItem("jwtToken", this.token);
        localStorage.setItem("currentUser", JSON.stringify(this.currentUser));
        localStorage.setItem("authMethod", "traditional");

        return {
          success: true,
          user: this.currentUser,
          token: this.token,
        };
      }

      return {
        success: false,
        error: response.data?.message || "Login failed",
      };
    } catch (error) {
      console.error("Traditional login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || "Login failed",
      };
    }
  }

  async logout() {
    try {
      // Call logout endpoint if available
      if (this.token) {
        ArchBaseService.init();
        ArchBaseService.setHeader();
        // Uncomment if backend has logout endpoint
        // await ArchBaseService.post('auth/logout', {});
      }
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      // Clear local storage
      this.clearAuth();
    }
  }

  clearAuth() {
    this.token = null;
    this.currentUser = null;
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("authMethod");

    // Clear other auth-related items
    const cookieKeys = [
      "transaction-list-filter",
      "remitter-list-filter",
      "receiver-list-filter",
      "receiver-show-deleted",
    ];

    cookieKeys.forEach((key) => {
      if (window.VueCookies) {
        window.VueCookies.remove(key);
      }
    });
  }

  async getToken() {
    // Check if token is still valid (you might want to add JWT expiration check)
    if (!this.token) {
      this.loadFromStorage();
    }
    return this.token;
  }

  async refreshToken() {
    try {
      if (!this.token) {
        throw new Error("No token to refresh");
      }

      ArchBaseService.init();
      ArchBaseService.setHeader();

      const response = await ArchBaseService.post("auth/refresh", {});

      if (response.data && response.data.token) {
        this.token = response.data.token;
        localStorage.setItem("jwtToken", this.token);
        return this.token;
      }

      throw new Error("Failed to refresh token");
    } catch (error) {
      console.error("Token refresh failed:", error);
      // If refresh fails, clear auth and redirect to login
      this.clearAuth();
      throw error;
    }
  }

  isAuthenticated() {
    return !!this.token;
  }

  getCurrentUser() {
    return this.currentUser;
  }

  hasRole(role) {
    if (!this.currentUser || !this.currentUser.roles) {
      return false;
    }
    return this.currentUser.roles.includes(role);
  }

  hasPermission(permission) {
    if (!this.currentUser || !this.currentUser.permissions) {
      return false;
    }
    return this.currentUser.permissions.includes(permission);
  }

  // Method to validate token (check if expired)
  isTokenExpired() {
    if (!this.token) return true;

    try {
      // Decode JWT token
      const base64Url = this.token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      const payload = JSON.parse(jsonPayload);

      // Check expiration
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return true;
    }
  }
}

export default new TraditionalAuthService();
